import { useEffect, useState } from 'react';
import { I18N } from './i18n';
import { clearTl } from './terms';

// country -> number/date locale, currency, unit system (metric | imperial)
export const COUNTRIES = {
  DE: ['de-DE', 'EUR', 'metric'], AT: ['de-AT', 'EUR', 'metric'], CH: ['de-CH', 'CHF', 'metric'], IT: ['it-IT', 'EUR', 'metric'],
  FR: ['fr-FR', 'EUR', 'metric'], ES: ['es-ES', 'EUR', 'metric'], PT: ['pt-PT', 'EUR', 'metric'], NL: ['nl-NL', 'EUR', 'metric'],
  BE: ['fr-BE', 'EUR', 'metric'], LU: ['fr-LU', 'EUR', 'metric'], PL: ['pl-PL', 'PLN', 'metric'], CZ: ['cs-CZ', 'CZK', 'metric'],
  DK: ['da-DK', 'DKK', 'metric'], SE: ['sv-SE', 'SEK', 'metric'], NO: ['nb-NO', 'NOK', 'metric'], GB: ['en-GB', 'GBP', 'metric'],
  IE: ['en-IE', 'EUR', 'metric'], US: ['en-US', 'USD', 'imperial'], CA: ['en-CA', 'CAD', 'metric'], AU: ['en-AU', 'AUD', 'metric'],
  BR: ['pt-BR', 'BRL', 'metric'], AL: ['sq-AL', 'ALL', 'metric'], XK: ['sq-XK', 'EUR', 'metric'], MK: ['mk-MK', 'MKD', 'metric'], MX: ['es-MX', 'MXN', 'metric'],
};
const KEY = 'giss_units';
// default country follows the app language; the device region only decides within that language (de-AT, en-US …)
const LANG_C = { de: 'DE', it: 'IT', en: 'GB', es: 'ES', pt: 'PT', fr: 'FR', sq: 'AL' };
const guess = () => {
  const lang = localStorage.getItem('baustelle_app_lang') || (navigator.language || 'de').slice(0, 2);
  const r = (navigator.language || '').split('-')[1]?.toUpperCase();
  return COUNTRIES[r] && COUNTRIES[r][0].startsWith(lang) ? r : LANG_C[lang] || 'DE';
};
export const getCountry = () => localStorage.getItem(KEY) || guess();
const C = () => COUNTRIES[getCountry()] || COUNTRIES.DE;
export const imperial = () => C()[2] === 'imperial';
export const locale = () => C()[0];
const symOf = (code, loc) => { try { return new Intl.NumberFormat(loc, { style: 'currency', currency: code }).formatToParts(0).find((p) => p.type === 'currency').value; } catch { return code; } };
export const cur = () => symOf(C()[1], C()[0]);

const fmt = (v, d = 2) => Number(v).toLocaleString(locale(), { minimumFractionDigits: d, maximumFractionDigits: d });
export const money = (v) => { try { return new Intl.NumberFormat(locale(), { style: 'currency', currency: C()[1] }).format(v); } catch { return `${fmt(v)} ${cur()}`; } };
// lengths in metres internally
export const lenU = () => (imperial() ? 'ft' : 'm');
export const len = (m, d = 1) => `${fmt(imperial() ? m * 3.28084 : m, d)} ${lenU()}`;
// depth input/labels: cm or inch
export const depU = () => (imperial() ? 'in' : 'cm');
export const depToM = (v) => (imperial() ? v * 0.0254 : v / 100);
export const mToDep = (m) => (imperial() ? m / 0.0254 : m * 100);
export const depth = (m) => `${fmt(mToDep(m), 0)} ${depU()}`;
// pressure in bar internally
export const pressU = () => (imperial() ? 'psi' : 'bar');
export const press = (bar, d = 2) => `${fmt(imperial() ? bar * 14.5038 : bar, imperial() ? 1 : d)} ${pressU()}`;

// apply: locale for all languages (dates & numbers everywhere), currency symbol in all texts
const ORIG = {};
const BAR = /(\(|\}\s?)bar\b/g;
export function applyUnits() {
  const loc = locale(), sym = cur(), imp = imperial();
  Object.entries(I18N).forEach(([l, o]) => {
    ORIG[l] ||= { locale: o.locale, strs: Object.fromEntries(Object.entries(o).filter(([k, v]) => typeof v === 'string' && ((v.includes('€') && !/play|trial|lic|pack|buy|subs/i.test(k)) || /(\(|\}\s?)bar\b/.test(v)))) };
    o.locale = loc;
    Object.entries(ORIG[l].strs).forEach(([k, v]) => { o[k] = (/play|trial|lic|pack|buy|subs/i.test(k) ? v : v.split('€').join(sym)).replace(BAR, `$1${imp ? 'psi' : 'bar'}`); });
  });
}

export function setCountry(c) {
  localStorage.setItem(KEY, c);
  applyUnits();
  clearTl();
  window.dispatchEvent(new Event('giss-units'));
}

export function useUnits() {
  const [c, setC] = useState(getCountry);
  useEffect(() => { const on = () => setC(getCountry()); window.addEventListener('giss-units', on); return () => window.removeEventListener('giss-units', on); }, []);
  return c;
}

applyUnits();

// dates / numbers formatted without an explicit locale follow the app country, not the device language
['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'].forEach((k) => {
  const orig = Date.prototype[k];
  Date.prototype[k] = function appLocaleDate(l, o) { return orig.call(this, l ?? locale(), o); }; // eslint-disable-line no-extend-native
});
const numOrig = Number.prototype.toLocaleString;
Number.prototype.toLocaleString = function appLocaleNum(l, o) { return numOrig.call(this, l ?? locale(), o); }; // eslint-disable-line no-extend-native
